// loadPrestoplayScript.js
'use strict';

import { player as playerDebug } from '../resource/debug.js';
import { buildLicenseHeaders } from '../resource/drmHelpers.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import {
  COM_APPLE_FPS,
  COM_MICROSOFT_PLAYREADY,
  COM_WIDEVINE_ALPHA,
  ORG_W3_CLEARKEY,
  PRESTOPLAY_DRM_ENV_NAME,
} from '../resource/drmConstants.js';
import getPlayReadyChallenge from '../resource/getPlayReadyChallenge.js';
import { getIsChinaAndroidBrowserExcludeHuawei } from '../resource/getUserAgent.js';
import { hexToBytes } from '../resource/responseDecryptionUtil.js';

const playerLog = playerDebug.extend('log:prestoplay');
const playerError = playerDebug.extend('error:prestoplay');

let clppInstance = null;
let loadPromise = null;
let isDrmEnvironmentRegistered = false;
let widevineDrmSystemConfig = null;
let isDrmResolverPatched = false;
let isClearKeyGenerateRequestPatched = false;
const clearKeyKeyIdByInitData = new WeakMap();
const clearKeySessions = new WeakSet();
const WIDEVINE_AUDIO_ROBUSTNESS_CUSTOM_DATA_KEY = 'widevineAudioRobustness';
const WIDEVINE_VIDEO_ROBUSTNESS_CUSTOM_DATA_KEY = 'widevineVideoRobustness';

const getClearKeyInitDataKey = initData => {
  const initDataKey = initData?.buffer || initData;
  return initDataKey && typeof initDataKey === 'object' ? initDataKey : null;
};

const getWidevineRobustnessCustomData = ({
  audioRobustness,
  videoRobustness,
}) => ({
  [WIDEVINE_AUDIO_ROBUSTNESS_CUSTOM_DATA_KEY]: audioRobustness,
  [WIDEVINE_VIDEO_ROBUSTNESS_CUSTOM_DATA_KEY]: videoRobustness,
});

const bytesToBase64Url = bytes =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

// China Android browsers (QQ, Baidu, UC, Xiaomi) use ClearKey but their CDMs
// reject cenc initDataType. Convert to keyids format per W3C EME spec §9.1.
const patchClearKeyGenerateRequest = () => {
  if (isClearKeyGenerateRequestPatched) return;
  if (!getIsChinaAndroidBrowserExcludeHuawei()) return;
  if (typeof MediaKeySession === 'undefined') return;

  const originalRequestMediaKeySystemAccess =
    navigator.requestMediaKeySystemAccess.bind(navigator);

  navigator.requestMediaKeySystemAccess = function (
    keySystem,
    supportedConfigurations
  ) {
    return originalRequestMediaKeySystemAccess(
      keySystem,
      supportedConfigurations
    ).then(access => {
      if (keySystem !== ORG_W3_CLEARKEY) return access;

      const originalCreateMediaKeys = access.createMediaKeys.bind(access);
      access.createMediaKeys = function () {
        return originalCreateMediaKeys().then(mediaKeys => {
          const originalCreateSession = mediaKeys.createSession.bind(mediaKeys);
          mediaKeys.createSession = function (sessionType) {
            const session = originalCreateSession(sessionType);
            clearKeySessions.add(session);
            return session;
          };
          return mediaKeys;
        });
      };
      return access;
    });
  };

  const originalGenerateRequest = MediaKeySession.prototype.generateRequest;

  MediaKeySession.prototype.generateRequest = function (
    initDataType,
    initData
  ) {
    if (initDataType === 'cenc' && clearKeySessions.has(this)) {
      const initDataKey = getClearKeyInitDataKey(initData);
      const keyIdHex = initDataKey
        ? clearKeyKeyIdByInitData.get(initDataKey)
        : '';
      if (keyIdHex && /^[0-9a-f]{32}$/i.test(keyIdHex)) {
        clearKeyKeyIdByInitData.delete(initDataKey);

        const keyIdBase64Url = bytesToBase64Url(hexToBytes(keyIdHex));
        const keyidsJson = JSON.stringify({ kids: [keyIdBase64Url] });
        const keyidsBuffer = new TextEncoder().encode(keyidsJson).buffer;

        return originalGenerateRequest.call(this, 'keyids', keyidsBuffer);
      }
    }

    return originalGenerateRequest.call(this, initDataType, initData);
  };

  isClearKeyGenerateRequestPatched = true;
};

const getWidevineRuntimeConfig = ({ drmSystemConfig, customData }) => {
  if (!drmSystemConfig) return drmSystemConfig;

  const audioRobustness =
    customData?.[WIDEVINE_AUDIO_ROBUSTNESS_CUSTOM_DATA_KEY];
  const videoRobustness =
    customData?.[WIDEVINE_VIDEO_ROBUSTNESS_CUSTOM_DATA_KEY];

  if (!audioRobustness && !videoRobustness) return drmSystemConfig;

  return {
    ...drmSystemConfig,
    ...(audioRobustness && { audioRobustness }),
    ...(videoRobustness && { videoRobustness }),
  };
};

const patchDrmResolver = ({ clpp, clearKeyDrmSystem }) => {
  if (isDrmResolverPatched) return;

  // Patch minified DRM resolver to include ClearKey and per-open Widevine robustness.
  // Note: .nk -> name may change after SDK upgrade.
  const resolveDrmSystems = clpp?._?.nk;
  if (typeof resolveDrmSystems !== 'function') {
    playerError('PRESTOplay ClearKey resolver hook is unavailable');
    return;
  }

  clpp._.nk = (drmEnvironment, customData) => {
    const resolvedDrmSystems = resolveDrmSystems(drmEnvironment, customData);
    const widevineRuntimeConfig = getWidevineRuntimeConfig({
      customData,
      drmSystemConfig: resolvedDrmSystems[clpp.drm.KeySystem.WIDEVINE],
    });

    const clearKeyRuntimeConfig = {};
    // PRESTOplay treats enumerable ClearKey fields as inline KID/key pairs.
    Object.defineProperties(clearKeyRuntimeConfig, {
      licenseUrl: { value: clearKeyDrmSystem.licenseUrl },
      modifiers: { value: clearKeyDrmSystem.modifiers },
    });

    return {
      ...resolvedDrmSystems,
      ...(widevineRuntimeConfig && {
        [clpp.drm.KeySystem.WIDEVINE]: widevineRuntimeConfig,
      }),
      [clpp.drm.KeySystem.CLEAR_KEY]: clearKeyRuntimeConfig,
    };
  };
  isDrmResolverPatched = true;
};

/**
 * Dynamically load PRESTOplay SDK, install components, and register DRM environment.
 * @returns {Promise<object>} The clpp namespace object.
 * @example
 * const clpp = await loadPrestoplayScript();
 * const player = new clpp.Player(videoElement, config);
 */
const loadPrestoplayScript = async () => {
  if (clppInstance) return clppInstance;
  if (loadPromise) return loadPromise;

  const log = playerLog.extend('load');

  loadPromise = (async () => {
    log('start loading prestoplay sdk');

    // HLS via MSE (Chrome) needs mux.js for TS→fMP4 transmux; PRESTOplay reads window.muxjs
    if (!window.muxjs) {
      // ESM dynamic import wraps CJS exports under .default
      const muxjsModule = await import('mux.js');
      window.muxjs = muxjsModule.default || muxjsModule;
    }

    const { clpp } = await import('@castlabs/prestoplay');
    await import('@castlabs/prestoplay/cl.mse');
    await import('@castlabs/prestoplay/cl.dash');
    await import('@castlabs/prestoplay/cl.hls');
    await import('@castlabs/prestoplay/cl.crypto');

    clpp.install(clpp.dash.DashComponent);
    clpp.install(clpp.hls.HlsComponent);
    clpp.install(clpp.crypto.CryptoComponent);

    registerSwagDrmEnvironment(clpp);

    clppInstance = clpp;
    log('done loading prestoplay sdk');
    return clpp;
  })();

  try {
    return await loadPromise;
  } catch (error) {
    loadPromise = null;
    playerError.extend('load')('load prestoplay sdk error', { error });
    throw error;
  }
};

// Store the first keyId from initData into session context for license request auth.
const extractKeyId = ({ drmInfo, initData, sessionContext }) => {
  const keyIds = Array.from(drmInfo?.keyIds || []);
  sessionContext.customData.kid = keyIds[0] || '';
  if (drmInfo?.keySystem === ORG_W3_CLEARKEY && keyIds[0]) {
    const initDataKey = getClearKeyInitDataKey(initData);
    if (initDataKey) clearKeyKeyIdByInitData.set(initDataKey, keyIds[0]);
  }
  return initData;
};

const registerSwagDrmEnvironment = clpp => {
  if (isDrmEnvironmentRegistered) return;

  const environment = new clpp.drm.DrmEnvironment(PRESTOPLAY_DRM_ENV_NAME);

  const widevineLicenseUrl = getResourceUrl({
    endpoint: `/drm/authorize/${COM_WIDEVINE_ALPHA}`,
  })?.href;

  const playReadyLicenseUrl = getResourceUrl({
    endpoint: `/drm/authorize/${COM_MICROSOFT_PLAYREADY}`,
  })?.href;

  const fairPlayLicenseUrl = getResourceUrl({
    endpoint: `/drm/authorize/${COM_APPLE_FPS}`,
  })?.href;

  const fairPlayCertificateUrl = getResourceUrl({
    endpoint: '/drm/certs/com.apple.fps.cer',
  })?.href;

  const clearKeyLicenseUrl = getResourceUrl({
    endpoint: `/drm/authorize/${ORG_W3_CLEARKEY}`,
  })?.href;

  widevineDrmSystemConfig = {
    licenseUrl: widevineLicenseUrl,
    modifiers: {
      transformInitData: extractKeyId,
      licenseRequest: async (configuration, request, sessionContext) => {
        const { token, clientId } = configuration?.drm?.customData || {};
        const kid = sessionContext?.customData?.kid || '';
        request.headers = {
          ...request.headers,
          ...buildLicenseHeaders({ kid, token, clientId }),
          'Content-Type': 'application/octet-stream',
        };
        return request;
      },
    },
  };
  environment.setDrmSystem(
    clpp.drm.KeySystem.WIDEVINE,
    widevineDrmSystemConfig
  );

  const fairPlayDrmSystem = {
    licenseUrl: fairPlayLicenseUrl,
    certificateUrl: fairPlayCertificateUrl,
    modifiers: {
      extractContentId: (_configuration, initData, sessionContext) => {
        // extractExtXKeyUri returns the raw skd string, avoiding the URL parsing that chokes on `uuid:HEX`.
        const skdUri = clpp.utils.FairplayUtils.extractExtXKeyUri(initData);
        const keyId = skdUri.split('://')[1] || '';
        sessionContext.customData.kid = keyId;
        return keyId;
      },
      licenseRequest: async (configuration, request, sessionContext) => {
        const { token, clientId, drmToken } =
          configuration?.drm?.customData || {};
        const kid = sessionContext?.customData?.kid || '';
        request.headers = {
          ...request.headers,
          ...buildLicenseHeaders({ kid, token, clientId }),
          ...(drmToken && { 'X-AxDRM-Message': drmToken }),
        };
        return request;
      },
    },
  };
  environment.setDrmSystem(clpp.drm.KeySystem.FAIRPLAY, fairPlayDrmSystem);

  const playReadyDrmSystem = {
    licenseUrl: playReadyLicenseUrl,
    modifiers: {
      transformInitData: extractKeyId,
      licenseRequest: async (configuration, request, sessionContext) => {
        const { token, clientId } = configuration?.drm?.customData || {};
        const kid = sessionContext?.customData?.kid || '';

        const { xmlChallenge, headers: playReadyHeaders } =
          getPlayReadyChallenge(request.body);

        if (xmlChallenge) {
          request.body = xmlChallenge;
        }

        request.headers = {
          ...request.headers,
          ...playReadyHeaders,
          ...buildLicenseHeaders({ kid, token, clientId }),
        };
        return request;
      },
    },
  };
  environment.setDrmSystem(clpp.drm.KeySystem.PLAYREADY, playReadyDrmSystem);

  const clearKeyDrmSystem = {
    licenseUrl: clearKeyLicenseUrl,
    modifiers: {
      transformInitData: extractKeyId,
      licenseRequest: (configuration, request, sessionContext) => {
        const { token, clientId } = configuration?.drm?.customData || {};
        const kid = sessionContext?.customData?.kid || '';
        request.headers = {
          ...request.headers,
          ...buildLicenseHeaders({ kid, token, clientId }),
          'Content-Type': 'application/json',
        };
        return request;
      },
    },
  };
  environment.setDrmSystem(clpp.drm.KeySystem.CLEAR_KEY, clearKeyDrmSystem);
  patchDrmResolver({ clpp, clearKeyDrmSystem });
  patchClearKeyGenerateRequest();

  clpp.drm.registerDrmEnvironment(environment);
  isDrmEnvironmentRegistered = true;
};

// PRESTOplay reads serverCertificate from the DRM environment config object,
// not from player.open()'s DrmConfiguration.
const setWidevineServerCertificate = ({ serverCertificate }) => {
  if (widevineDrmSystemConfig) {
    widevineDrmSystemConfig.serverCertificate = serverCertificate;
  }
};

export {
  getWidevineRobustnessCustomData,
  loadPrestoplayScript,
  setWidevineServerCertificate,
};
